import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import PageLayout from '@/components/layout/PageLayout';
import { EditEmployeeContractForm } from '@/components/features/employee/editEmployeeContractForm';
import { EmployeeSectionNav } from '@/components/features/employee/EmployeeSectionNav';
import { useEmployeeActiveContract } from '@/hooks/employee/useEmployeeActiveContract';
import { Text } from '@/components/ui/text/Text';
import { useTranslation } from '@/i18n';
import { buildEmployeeFullName, formatEmployeeIdentity } from '@/utils';
import { SECTIONS } from '@/types/sections';


export const EmployeeActiveContract: React.FC = () => {
  const { t: translate } = useTranslation();
  const { employeeId } = useParams<{ employeeId: string }>();
  const numericEmployeeId = employeeId ? Number(employeeId) : undefined;

  const {
    isLoading,
    updating,
    hasActiveContract,
    employeeProfile,
    handleCancel,
    handleUpdate,
    contractStatusOptions,
    contractTypeOptions,
    contractModalityOptions,
    companyOptions,
    areaOptions,
    positionOptions,
    costCenterOptions,
    unionOptions,
    form,
  } = useEmployeeActiveContract(numericEmployeeId);

  // ASIS-5433: apellido paterno, apellido materno, nombre y rut del colaborador.
  // El orden y el formato del RUT los centraliza formatEmployeeIdentity, igual que en el
  // detalle de contrato y el perfil.
  const dynamicTitle = useMemo(() => {
    const identity = formatEmployeeIdentity(
      buildEmployeeFullName(employeeProfile ?? {}),
      employeeProfile?.rutFull,
    );
    return identity ? translate('employee:breadcrumb.contractDetail', { identity }) : undefined;
  }, [employeeProfile, translate]);

  const sectionNav = numericEmployeeId ? (
    <EmployeeSectionNav employeeId={numericEmployeeId} activeSection="activeContract" />
  ) : undefined;

  return (
    <PageLayout dynamicTitle={dynamicTitle} isLoading={isLoading} headerContent={sectionNav}>
      <div data-testid="employee-active-contract-page">
        {hasActiveContract ? (
          <EditEmployeeContractForm
            contractForm={form}
            contractStatusOptions={contractStatusOptions}
            contractTypeOptions={contractTypeOptions}
            contractModalityOptions={contractModalityOptions}
            companyOptions={companyOptions}
            areaOptions={areaOptions}
            positionOptions={positionOptions}
            costCenterOptions={costCenterOptions}
            unionOptions={unionOptions}
            updating={updating}
            onCancel={handleCancel}
            onSave={handleUpdate}
            permissionSection={SECTIONS.CONDICIONES_LABORALES}
          />
        ) : (
          <div className="py-10 text-center" data-testid="employee-active-contract-empty">
            <Text variant="body-regular">
              {translate('employee:contracts.messages.noActiveContract')}
            </Text>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default EmployeeActiveContract;
